/**
 * ORGANIZATION vault flows and sharing: PURE builders of REST request
 * bodies sent from client to server.
 *
 * ZERO-KNOWLEDGE INVARIANT: everything produced by these builders is OPAQUE — the
 * vault key travels SEALED to member public key (ECIES),
 * items and names travel as AES-GCM envelopes, and no body field
 * serialized contains plaintext or key material. Explicitly tested
 * in `__tests__/org-flows.test.ts` (each body serialized and searched for
 * fuga).
 *
 * Pure functions (client-side crypto, no network): web layer calls them and
 * passes `body` as-is to `POST /api/vault/org`, `POST /api/vault/org/rotate`
 * y `POST /api/vault/share`.
 */

import {
  createOrgVault,
  distributeVaultKey,
  openMembership,
  type OrgMemberPublic,
  rotateOrgVault,
} from "../crypto/org";
import { encryptVaultName, reencryptItem } from "../crypto/vault";
import { openSharedSecret, shareSecret } from "../crypto/sharing";
import type { SealedToPublicKey } from "../crypto/asymmetric";
import type { ProtectedItem, VaultItemPlain, VaultMembership } from "../crypto/types";

/** Member key distribution in camelCase REST contract form. */
export interface MembershipShareBody {
  readonly userId: string;
  readonly role: string;
  readonly sealedVaultKey: string;
}

/** Body of `POST /api/vault/org` (fully opaque). */
export interface CreateOrgVaultRequestBody {
  readonly id: string;
  readonly organizationId: string;
  readonly nameEnvelope?: string;
  readonly memberships: MembershipShareBody[];
}

/** Body of `POST /api/vault/org/rotate` (fully opaque). */
export interface RotateOrgVaultRequestBody {
  readonly vaultId: string;
  readonly newEpoch: number;
  readonly memberships: MembershipShareBody[];
  readonly items: { readonly id: string; readonly envelope: string; readonly rev: number }[];
}

/** Body of `POST /api/vault/share` (envelope sealed to recipient). */
export interface ShareSecretRequestBody {
  readonly id: string;
  readonly toUserId: string;
  readonly sealed: string;
  readonly expiresAt?: string;
}

function toShareBody(memberships: VaultMembership[]): MembershipShareBody[] {
  return memberships.map((m) => ({
    userId: m.userId,
    role: m.role,
    sealedVaultKey: m.sealedVaultKey,
  }));
}

export interface CreateOrgVaultFlowInput {
  readonly vaultId: string;
  readonly organizationId: string;
  /** Team name (plaintext ONLY in client; travels encrypted in nameEnvelope). */
  readonly name: string;
  /** Members (public keys) to distribute key to, including creator. */
  readonly members: OrgMemberPublic[];
}

export interface CreateOrgVaultFlowResult {
  readonly body: CreateOrgVaultRequestBody;
  /** Plaintext vault key — ONLY in creator memory. */
  readonly vaultKey: Uint8Array;
  readonly keyEpoch: number;
}

/** Creates organization vault: generates key, distributes it and encrypts name. */
export async function buildCreateOrgVaultRequest(
  input: CreateOrgVaultFlowInput,
): Promise<CreateOrgVaultFlowResult> {
  const created = await createOrgVault(input.vaultId, input.members);
  const nameEnvelope = await encryptVaultName(created.vaultKey, input.vaultId, input.name);
  return {
    body: {
      id: input.vaultId,
      organizationId: input.organizationId,
      nameEnvelope,
      memberships: toShareBody(created.memberships),
    },
    vaultKey: created.vaultKey,
    keyEpoch: created.keyEpoch,
  };
}

export interface RotateOrgVaultFlowInput {
  readonly vaultId: string;
  /** Current epoch (new epoch will be +1). */
  readonly currentEpoch: number;
  /** Current vault key (to re-encrypt items). */
  readonly oldVaultKey: Uint8Array;
  /** Members RETAINING access (revoked member simply omitted). */
  readonly remainingMembers: OrgMemberPublic[];
  /** Current item envelopes (re-encrypted under new key). */
  readonly items: ProtectedItem[];
  /** Plaintext vault name (re-encrypted with new key), if present. */
  readonly name?: string;
}

export interface RotateOrgVaultFlowResult {
  readonly body: RotateOrgVaultRequestBody;
  readonly vaultKey: Uint8Array;
  readonly keyEpoch: number;
}

/**
 * Rotates vault key after revoking member: new key (epoch+1),
 * distribution ONLY to remaining members and re-encryption of all items. Revoked
 * member, even retaining old key, cannot open new epoch envelopes.
 */
export async function buildRotateOrgVaultRequest(
  input: RotateOrgVaultFlowInput,
): Promise<RotateOrgVaultFlowResult> {
  const rotated = await rotateOrgVault(input.vaultId, input.currentEpoch, input.remainingMembers);
  const items: RotateOrgVaultRequestBody["items"] = [];
  for (const item of input.items) {
    const re = await reencryptItem(input.oldVaultKey, rotated.vaultKey, item);
    items.push({ id: re.id, envelope: re.envelope, rev: re.rev });
  }
  return {
    body: {
      vaultId: input.vaultId,
      newEpoch: rotated.keyEpoch,
      memberships: toShareBody(rotated.memberships),
      items,
    },
    vaultKey: rotated.vaultKey,
    keyEpoch: rotated.keyEpoch,
  };
}

export interface ShareSecretFlowInput {
  readonly id: string;
  readonly fromUserId: string;
  readonly toUserId: string;
  /** Recipient's ECDH public key (raw base64) — published by server. */
  readonly recipientPublicKey: string;
  /** Plaintext secret (ONLY in client; travels sealed). */
  readonly secret: VaultItemPlain;
  readonly expiresAt?: string;
}

/** Seals point-in-time secret to recipient (ECIES). Server only forwards it. */
export async function buildShareSecretRequest(
  input: ShareSecretFlowInput,
): Promise<ShareSecretRequestBody> {
  const shared = await shareSecret(
    input.fromUserId,
    input.toUserId,
    input.recipientPublicKey,
    input.secret,
    input.expiresAt,
  );
  return {
    id: input.id,
    toUserId: input.toUserId,
    sealed: JSON.stringify(shared.sealed),
    ...(input.expiresAt ? { expiresAt: input.expiresAt } : {}),
  };
}

/** Local row (PowerSync) of `vault_membership`, as received from sync. */
export interface VaultMembershipRow {
  readonly vault_id: string | null;
  readonly user_id: string | null;
  readonly role: string | null;
  readonly sealed_vault_key: string | null;
  readonly key_epoch: number | null;
}

/** Opens vault key from MY synchronized membership row. */
export async function openMembershipRow(
  privateKeyB64: string,
  row: VaultMembershipRow,
): Promise<Uint8Array> {
  if (!row.vault_id || !row.user_id || !row.sealed_vault_key) {
    throw new Error("membership row is incomplete");
  }
  const membership: VaultMembership = {
    vaultId: row.vault_id,
    userId: row.user_id,
    // Role does not take part in AAD; default value weakens nothing.
    role: row.role === "owner" || row.role === "admin" ? row.role : "member",
    sealedVaultKey: row.sealed_vault_key,
    keyEpoch: row.key_epoch ?? 1,
  };
  return openMembership(privateKeyB64, membership);
}

/** Local row (PowerSync) of `vault_share` synced to recipient. */
export interface VaultShareRow {
  readonly id: string;
  readonly from_user_id: string | null;
  readonly to_user_id: string | null;
  readonly sealed: string | null;
}

/** Opens secret shared with me (envelope sealed to my public key). */
export async function openIncomingShareRow(
  privateKeyB64: string,
  row: VaultShareRow,
): Promise<VaultItemPlain> {
  if (!row.sealed || !row.from_user_id || !row.to_user_id) {
    throw new Error("share row is incomplete");
  }
  // Trust boundary: row comes from OUR local DB (written by sharing
  // flow); if corrupted, AEAD open fails anyway.
  // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
  const sealed = JSON.parse(row.sealed) as SealedToPublicKey;
  return openSharedSecret(privateKeyB64, {
    sealed,
    fromUserId: row.from_user_id,
    toUserId: row.to_user_id,
  });
}

export { distributeVaultKey };
export type { OrgMemberPublic };
